import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiService } from '../../services/apiService';

interface Node {
  id: string;
  label?: string;
  party?: string | null;
}
interface Edge {
  source: string;
  target: string;
  weight?: number;
}
interface GraphData {
  nodes: Node[];
  edges: Edge[];
}

// Lightweight force simulation (very basic) to avoid heavy deps
function simulateLayout(nodes: Node[], edges: Edge[], width: number, height: number, iterations: number = 200) {
  const pos: Record<string, { x: number; y: number; vx: number; vy: number; }> = {};
  const ids = nodes.map(n => n.id);
  const centerX = width / 2;
  const centerY = height / 2;
  // init positions on circle
  const R = Math.min(width, height) * 0.35;
  ids.forEach((id, i) => {
    const theta = (i / Math.max(1, ids.length)) * Math.PI * 2;
    pos[id] = { x: centerX + R * Math.cos(theta), y: centerY + R * Math.sin(theta), vx: 0, vy: 0 };
  });
  // spring and repulsion constants (tuned for small graphs)
  const k = 0.02; // spring
  const rep = 5000; // repulsion strength
  const damping = 0.85;
  const dt = 1;
  const nodeList = ids.map(id => ({ id }));
  for (let it = 0; it < iterations; it++) {
    // repulsion
    for (let i = 0; i < nodeList.length; i++) {
      const a = nodeList[i].id;
      for (let j = i + 1; j < nodeList.length; j++) {
        const b = nodeList[j].id;
        const dx = pos[a].x - pos[b].x;
        const dy = pos[a].y - pos[b].y;
        const dist2 = Math.max(1, dx*dx + dy*dy);
        const f = rep / dist2;
        const fx = f * (dx / Math.sqrt(dist2));
        const fy = f * (dy / Math.sqrt(dist2));
        pos[a].vx += fx * dt;
        pos[a].vy += fy * dt;
        pos[b].vx -= fx * dt;
        pos[b].vy -= fy * dt;
      }
    }
    // springs
    edges.forEach(e => {
      const a = e.source; const b = e.target;
      const dx = pos[b].x - pos[a].x;
      const dy = pos[b].y - pos[a].y;
      const fx = k * dx; const fy = k * dy;
      pos[a].vx += fx * dt; pos[a].vy += fy * dt;
      pos[b].vx -= fx * dt; pos[b].vy -= fy * dt;
    });
    // integrate + damping + gravity to center
    ids.forEach(id => {
      // pull toward center mildly
      const gx = (centerX - pos[id].x) * 0.001;
      const gy = (centerY - pos[id].y) * 0.001;
      pos[id].vx = (pos[id].vx + gx) * damping;
      pos[id].vy = (pos[id].vy + gy) * damping;
      pos[id].x += pos[id].vx * dt;
      pos[id].y += pos[id].vy * dt;
    });
  }
  return pos;
}

const partyColor = (p?: string | null): string => {
  switch ((p || '').toLowerCase()) {
    case 'republican':
      return '#d32f2f';
    case 'democratic':
      return '#1976d2';
    case 'independent':
      return '#7b1fa2';
    default:
      return '#616161';
  }
};

export const SponsorNetworkGraph: React.FC<{ bioguideId: string } > = ({ bioguideId }) => {
  const [data, setData] = useState<GraphData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [tick, setTick] = useState(0);

  const width = 640; const height = 420;

  const positions = useMemo(() => {
    if (!data) return {} as Record<string, { x: number; y: number; vx: number; vy: number }>;
    return simulateLayout(data.nodes, data.edges, width, height, 180);
  }, [data, tick]);

  const load = async () => {
    try {
      setError(null);
      const resp = await apiService.getSponsorGraph(bioguideId);
      const d = resp?.data;
      const nodes = Array.isArray(d?.nodes) ? d.nodes : [];
      const edges = Array.isArray(d?.edges) ? d.edges : [];
      setData({ nodes, edges });
      setTick(x => x + 1);
    } catch (e: any) {
      setError(e?.message || 'Failed to load graph');
      setData({ nodes: [], edges: [] });
    }
  };

  useEffect(() => { load(); }, [bioguideId]);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h6">Sponsor Network</Typography>
        <Box>
          <Tooltip title="Reload">
            <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Co-sponsorship edges. Node color indicates party.">
            <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </Box>
      {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <svg ref={svgRef} width={width} height={height}>
          {/* edges */}
          <g>
            {(data?.edges || []).map((e, idx) => {
              const a = positions[e.source];
              const b = positions[e.target];
              if (!a || !b) return null;
              const w = Math.max(1, Math.min(6, (e.weight || 1)));
              return <line key={idx} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#bbb" strokeWidth={w * 0.6} />;
            })}
          </g>
          {/* nodes */}
          <g>
            {(data?.nodes || []).map((n, idx) => {
              const p = positions[n.id]; if (!p) return null;
              const r = n.id === bioguideId ? 10 : 7;
              const color = partyColor(n.party);
              return (
                <g key={n.id}>
                  <circle cx={p.x} cy={p.y} r={r} fill={color} stroke="#fff" strokeWidth={1.5} />
                  {n.label && (
                    <text x={p.x + r + 4} y={p.y + 4} fontSize={12} fill="#333">{n.label}</text>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      </Box>
    </Box>
  );
};

export default SponsorNetworkGraph;
